// Localização: frontend/src/components/Financeiro/ModalRecebimento.jsx
// Modal para registrar recebimento (total ou parcial)

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import { useContasBancarias } from '../../hooks/useFinanceiro';
import { DollarSign, Calendar, CreditCard, Plus, Minus } from 'lucide-react';

const ModalRecebimento = ({ isOpen, onClose, conta, onReceber }) => {
    const [tipoRecebimento, setTipoRecebimento] = useState('total'); // 'total' ou 'parcial'
    const [formData, setFormData] = useState({
        valor: '',
        data_recebimento: '',
        forma_recebimento: '',
        juros: '',
        multa: ''
    });
    const [errors, setErrors] = useState({});
    const hasLoadedContasRef = useRef(false);

    // Hook para buscar contas bancárias
    const { contas, fetchContas, loading: loadingContas } = useContasBancarias();

    useEffect(() => {
        if (isOpen) {
            // Carregar contas apenas uma vez quando o modal abrir
            if (!hasLoadedContasRef.current || contas.length === 0) {
                fetchContas().catch(err => {
                    console.error('[ModalRecebimento] Erro ao carregar contas bancárias:', err);
                });
                hasLoadedContasRef.current = true;
            }

            if (conta) {
                const hoje = new Date().toISOString().split('T')[0];
                setFormData({
                    valor: conta.valor_pendente > 0 ? conta.valor_pendente.toString() : '',
                    data_recebimento: hoje,
                    forma_recebimento: conta.conta_bancaria_id || conta.conta_bancaria?.id || conta.forma_recebimento || '',
                    juros: '',
                    multa: ''
                });
                setTipoRecebimento('total');
                setErrors({});
            }
        } else {
            // Resetar flag quando o modal fechar
            hasLoadedContasRef.current = false;
        }
    }, [isOpen, conta]); // Removido fetchContas das dependências

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    };

    // Calcular valor total a receber
    const calcularValorTotal = () => {
        const valorBase = tipoRecebimento === 'total'
            ? parseFloat(conta?.valor_pendente || 0)
            : parseFloat(formData.valor || 0);
        const valorJuros = parseFloat(formData.juros || 0);
        const valorMulta = parseFloat(formData.multa || 0);
        return valorBase + valorJuros + valorMulta;
    };

    const validate = () => {
        const newErrors = {};

        if (tipoRecebimento === 'parcial') {
            if (!formData.valor || parseFloat(formData.valor) <= 0) {
                newErrors.valor = 'Valor deve ser maior que zero';
            } else if (parseFloat(formData.valor) > parseFloat(conta.valor_pendente)) {
                newErrors.valor = `Valor não pode ser maior que o pendente (${formatCurrency(conta.valor_pendente)})`;
            }
        }

        if (!formData.data_recebimento) {
            newErrors.data_recebimento = 'Data de recebimento é obrigatória';
        }

        if (!formData.forma_recebimento) {
            newErrors.forma_recebimento = 'Conta bancária é obrigatória';
        }

        // Validar juros e multa
        if (formData.juros && parseFloat(formData.juros) < 0) {
            newErrors.juros = 'Juros não podem ser negativos';
        }
        if (formData.multa && parseFloat(formData.multa) < 0) {
            newErrors.multa = 'Multa não pode ser negativa';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            const data = {
                data_recebimento: formData.data_recebimento,
                forma_recebimento: null, // Removido - agora usa conta_bancaria_id
                conta_bancaria_id: formData.forma_recebimento || null,
                juros: parseFloat(formData.juros || 0),
                multa: parseFloat(formData.multa || 0)
            };

            if (tipoRecebimento === 'parcial') {
                data.valor = parseFloat(formData.valor);
            }

            await onReceber(conta.id, tipoRecebimento, data);
            onClose();
        } catch (error) {
            console.error('Erro ao registrar recebimento:', error);
        }
    };

    if (!conta) return null;

    const valorTotal = calcularValorTotal();

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Registrar Recebimento">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações da Conta */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">{conta.descricao}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600">Valor Total</p>
                            <p className="font-semibold text-gray-900">{formatCurrency(conta.valor_total)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Valor Recebido</p>
                            <p className="font-semibold text-green-600">{formatCurrency(conta.valor_recebido)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Valor Pendente</p>
                            <p className="font-semibold text-orange-600">{formatCurrency(conta.valor_pendente)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Vencimento</p>
                            <p className="font-semibold text-gray-900">
                                {new Date(conta.data_vencimento).toLocaleDateString('pt-BR')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tipo de Recebimento */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Recebimento
                    </label>
                    <div className="flex gap-4">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="total"
                                checked={tipoRecebimento === 'total'}
                                onChange={(e) => setTipoRecebimento(e.target.value)}
                                className="mr-2"
                            />
                            <span>Recebimento Total ({formatCurrency(conta.valor_pendente)})</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="parcial"
                                checked={tipoRecebimento === 'parcial'}
                                onChange={(e) => setTipoRecebimento(e.target.value)}
                                className="mr-2"
                            />
                            <span>Recebimento Parcial</span>
                        </label>
                    </div>
                </div>

                {/* Valor (apenas para parcial) */}
                {tipoRecebimento === 'parcial' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <DollarSign className="inline h-4 w-4 mr-1" />
                            Valor do Recebimento <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            name="valor"
                            value={formData.valor}
                            onChange={handleChange}
                            step="0.01"
                            min="0.01"
                            max={conta.valor_pendente}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.valor ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder={`Máximo: ${formatCurrency(conta.valor_pendente)}`}
                        />
                        {errors.valor && (
                            <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                            Valor restante após recebimento: {formatCurrency(
                                formData.valor ? conta.valor_pendente - parseFloat(formData.valor) : conta.valor_pendente
                            )}
                        </p>
                    </div>
                )}

                {/* Juros e Multa */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="juros" className="block text-sm font-medium text-gray-700 mb-1">
                            <Plus className="inline h-3 w-3 mr-1" />
                            Juros (R$)
                        </label>
                        <input
                            type="number"
                            id="juros"
                            name="juros"
                            value={formData.juros}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.juros ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0,00"
                        />
                        {errors.juros && (
                            <p className="mt-1 text-sm text-red-600">{errors.juros}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="multa" className="block text-sm font-medium text-gray-700 mb-1">
                            <Minus className="inline h-3 w-3 mr-1" />
                            Multa (R$)
                        </label>
                        <input
                            type="number"
                            id="multa"
                            name="multa"
                            value={formData.multa}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.multa ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0,00"
                        />
                        {errors.multa && (
                            <p className="mt-1 text-sm text-red-600">{errors.multa}</p>
                        )}
                    </div>
                </div>

                {/* Resumo do valor total */}
                {(formData.juros || formData.multa) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Valor base:</span>
                                <span className="font-medium">{formatCurrency(
                                    tipoRecebimento === 'total' ? conta.valor_pendente : parseFloat(formData.valor || 0)
                                )}</span>
                            </div>
                            {formData.juros && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">+ Juros:</span>
                                    <span className="font-medium">{formatCurrency(parseFloat(formData.juros))}</span>
                                </div>
                            )}
                            {formData.multa && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">+ Multa:</span>
                                    <span className="font-medium">{formatCurrency(parseFloat(formData.multa))}</span>
                                </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-blue-300">
                                <span className="font-semibold text-gray-900">Total a Receber:</span>
                                <span className="font-bold text-emerald-600 text-lg">{formatCurrency(valorTotal)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data de Recebimento */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="inline h-4 w-4 mr-1" />
                        Data de Recebimento <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="data_recebimento"
                        value={formData.data_recebimento}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.data_recebimento ? 'border-red-500' : 'border-gray-300'
                            }`}
                    />
                    {errors.data_recebimento && (
                        <p className="mt-1 text-sm text-red-600">{errors.data_recebimento}</p>
                    )}
                </div>

                {/* Forma de Recebimento (Conta Bancária) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <CreditCard className="inline h-4 w-4 mr-1" />
                        Forma de Recebimento (Conta Bancária) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <select
                            name="forma_recebimento"
                            value={formData.forma_recebimento}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.forma_recebimento ? 'border-red-500' : 'border-gray-300'
                                }`}
                            disabled={loadingContas}
                        >
                            <option value="">Selecione uma conta bancária...</option>
                            {contas.map(contaBancaria => (
                                <option key={contaBancaria.id} value={contaBancaria.id}>
                                    {contaBancaria.nome} - {contaBancaria.banco} {contaBancaria.agencia ? `(${contaBancaria.agencia})` : ''}
                                </option>
                            ))}
                        </select>
                        {loadingContas && (
                            <div className="absolute right-3 top-2.5">
                                <LoadingIndicator size="sm" />
                            </div>
                        )}
                    </div>
                    {errors.forma_recebimento && (
                        <p className="mt-1 text-sm text-red-600">{errors.forma_recebimento}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                        Selecione a conta bancária onde o recebimento será registrado
                    </p>
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Cancelar
                    </button>
                    <PrimaryButton type="submit" className="px-4 py-2 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        {tipoRecebimento === 'total' ? 'Registrar Recebimento Total' : 'Registrar Recebimento Parcial'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default ModalRecebimento;
